import { motion } from "framer-motion";
import { Users, Zap, MapPin, Calendar, Flag } from "lucide-react";

const AboutSection = () => {
  const values = [
    {
      icon: Users,
      title: "Inclusive by Design",
      description: "AI that works for everyone, not just Wall Street.",
    },
    {
      icon: Zap,
      title: "Execution First",
      description: "We ship. Fast. Then iterate.",
    },
    {
      icon: MapPin,
      title: "Australian-Built",
      description: "Proudly founded in Western Australia.",
    },
  ];

  return (
    <section
      id="about"
      className="relative py-16 md:py-24 overflow-hidden"
      data-testid="about-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-16"
        >
          <span className="inline-block px-3 md:px-4 py-2 rounded-full glass text-xs md:text-sm font-mono text-[#00D4FF] mb-3 md:mb-4">
            ABOUT US
          </span>
          <h2 className="font-['Unbounded'] text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-3 md:mb-4">
            Built by
            <br />
            <span className="gradient-text">Future Gen AI</span>
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl mx-auto">
            Indigenous-founded. Community-driven. Built for everyone.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Story */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <p className="text-base md:text-lg text-[#A1A1AA] leading-relaxed mb-6 md:mb-8">
              Future Gen AI was founded by{" "}
              <span className="text-white font-semibold">Dennis Simmons</span>{" "}
              with a clear mission: bring the power of artificial intelligence
              to communities historically excluded from the tech revolution.
            </p>
            <p className="text-base md:text-lg text-[#A1A1AA] leading-relaxed mb-6 md:mb-8">
              <span className="text-white font-semibold">GHOST</span> is our
              flagship product — a trading platform that levels the playing
              field. Dennis is a self-taught technologist and proud{" "}
              <span className="text-[#00D4FF]">Indigenous Australian</span>.
              We're not building AI for the sake of it — we're building tools
              that create real opportunity.
            </p>

            {/* Values - Responsive grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="p-4 rounded-xl md:rounded-2xl glass ice-hover"
                >
                  <value.icon
                    size={20}
                    className="text-[#00D4FF] mb-2 md:mb-3 md:w-6 md:h-6"
                  />
                  <h4 className="font-semibold text-white text-sm mb-1">
                    {value.title}
                  </h4>
                  <p className="text-xs text-[#A1A1AA]">{value.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Founder card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center order-1 lg:order-2"
          >
            <div className="p-6 md:p-8 rounded-2xl md:rounded-3xl glass ice-hover max-w-sm w-full text-center">
              {/* Avatar */}
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#00D4FF] to-[#8B5CF6] flex items-center justify-center mx-auto mb-4 md:mb-6 text-2xl md:text-3xl font-bold text-white shadow-[0_0_40px_rgba(0,212,255,0.4)]">
                DS
              </div>

              {/* Name & title */}
              <h3 className="font-['Unbounded'] text-lg md:text-xl font-bold text-white mb-2">
                Dennis Simmons
              </h3>
              <p className="text-[#A1A1AA] text-sm md:text-base mb-4 md:mb-6">
                Founder & Commander — Future Gen AI
              </p>

              {/* Details - Responsive wrap */}
              <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                <span className="inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 rounded-full glass text-[10px] md:text-xs">
                  <Calendar size={10} className="text-[#00D4FF] md:w-3 md:h-3" />
                  Founded 2025
                </span>
                <span className="inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 rounded-full glass text-[10px] md:text-xs">
                  <Flag size={10} className="text-[#00D4FF] md:w-3 md:h-3" />
                  Western Australia
                </span>
                <span className="inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 rounded-full glass text-[10px] md:text-xs">
                  <Zap size={10} className="text-[#00D4FF] md:w-3 md:h-3" />
                  AI-First
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
